import {
  LayoutDashboard, Contact2, FileCheck, CalendarDays, BarChart3, Trophy,
  Share2, Network, Package, Settings, Sparkles, LifeBuoy, SlidersHorizontal,
  ShieldCheck, Megaphone, CheckCheck, Ear, GitBranch, CreditCard, Newspaper,
  Sun, Moon, Monitor, Globe, Users2, UserCheck, Hash, BookOpen, Brain,
  CheckSquare, ImageIcon, PenSquare, MessageSquare, Search,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useOrgPermissions, type ModuleKey, type AccessLevel } from "@/hooks/useOrgPermissions";
import { PermissionGatedNavItem } from "./PermissionGatedNavItem";
import { OrgSwitcher } from "./OrgSwitcher";
import type { LucideIcon } from "lucide-react";

interface SidebarProps {
  activeView: string;
  onViewChange: (view: string) => void;
}

interface InstitutionModule {
  module: ModuleKey;
  level: AccessLevel;
  icon: LucideIcon;
  label: string;
  view: string;
}

const INSTITUTION_MODULES: InstitutionModule[] = [
  { module: 'dashboard',          level: 'view', icon: LayoutDashboard, label: 'Dashboard',        view: 'dashboard' },
  { module: 'media_registry',     level: 'view', icon: Contact2,        label: 'Media Registry',   view: 'media-registry' },
  { module: 'events',             level: 'view', icon: CalendarDays,    label: 'Events',            view: 'events' },
  { module: 'accreditation',      level: 'view', icon: FileCheck,       label: 'Accreditation',    view: 'accreditation' },
  { module: 'journalist_portal',  level: 'view', icon: Users2,          label: 'Press Review',     view: 'press-review' },
  { module: 'journalist_portal',  level: 'view', icon: Search,          label: 'Journalist Directory', view: 'journalist-directory' },
  { module: 'coverage_analytics', level: 'view', icon: BarChart3,       label: 'Analytics',        view: 'analytics' },
  { module: 'awards',             level: 'view', icon: Trophy,          label: 'Awards',            view: 'awards' },
  { module: 'social_media',       level: 'view', icon: Share2,          label: 'Social Hub',        view: 'social-hub' },
  { module: 'social_media',       level: 'edit', icon: PenSquare,       label: 'Create Post',       view: 'posts' },
  { module: 'social_media',       level: 'view', icon: BarChart3,       label: 'Social Analytics',  view: 'social-analytics' },
  { module: 'social_media',       level: 'view', icon: CalendarDays,    label: 'Content Calendar', view: 'calendar' },
  { module: 'social_media',       level: 'view', icon: ImageIcon,       label: 'Media Library',    view: 'media-library' },
  { module: 'social_media',       level: 'view', icon: Megaphone,       label: 'Campaigns',        view: 'campaigns' },
  { module: 'social_media',       level: 'edit', icon: CheckCheck,      label: 'Approvals',        view: 'approvals' },
  { module: 'social_media',       level: 'edit', icon: GitBranch,       label: 'Workflows',        view: 'workflows' },
  { module: 'social_media',       level: 'view', icon: Brain,           label: 'Intelligence',     view: 'intelligence' },
  { module: 'social_media',       level: 'view', icon: Sparkles,        label: 'AI Assistant',     view: 'ai-assistant' },
  { module: 'social_media',       level: 'view', icon: MessageSquare,   label: 'Messages',          view: 'social-dms' },
  { module: 'organogram',         level: 'view', icon: Network,         label: 'Organisation',     view: 'organization' },
  { module: 'organogram',         level: 'view', icon: Users2,          label: 'Team Members',     view: 'org-users' },
  { module: 'media_kits',         level: 'view', icon: Package,         label: 'Media Kits',       view: 'media-kits' },
  { module: 'press_releases',     level: 'view', icon: Newspaper,       label: 'Press Releases',   view: 'press-releases' },
];

const SUPPORTED_LANGS = [
  { code: 'en', label: 'EN' },
  { code: 'fr', label: 'FR' },
  { code: 'es', label: 'ES' },
  { code: 'pt', label: 'PT' },
  { code: 'ar', label: 'AR' },
  { code: 'sw', label: 'SW' },
]

export const Sidebar = ({ activeView, onViewChange }: SidebarProps) => {
  const { t, i18n } = useTranslation();
  const { orgId, orgType, can, loading } = useOrgPermissions();
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const { data: isAppAdmin } = useQuery({
    queryKey: ['is_app_admin'],
    queryFn: async () => {
      const { data } = await supabase.rpc('is_app_admin' as any)
      return data as boolean
    },
    staleTime: 5 * 60 * 1000,
  });

  // Load org branding for sidebar logo + name
  const { data: orgBrand } = useQuery({
    queryKey: ['org-sidebar-brand', orgId],
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data } = await (supabase
        .from('organizations' as any)
        .select('name, logo_url, brand_primary')
        .eq('id', orgId!)
        .single() as any)
      return data as { name: string; logo_url: string | null; brand_primary: string | null } | null
    },
  });

  const btnClass = (view: string) =>
    cn(
      "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-base text-sm font-medium",
      activeView === view
        ? "bg-sidebar-accent text-sidebar-accent-foreground shadow-sm"
        : "text-sidebar-foreground hover:bg-sidebar-accent/50"
    );

  const themeIcon = !mounted ? null
    : theme === 'dark' ? <Moon className="w-3.5 h-3.5" />
    : theme === 'light' ? <Sun className="w-3.5 h-3.5" />
    : <Monitor className="w-3.5 h-3.5" />;

  const cycleTheme = () => {
    if (theme === 'light') setTheme('dark');
    else if (theme === 'dark') setTheme('system');
    else setTheme('light');
  };

  return (
    <aside className="w-64 bg-sidebar border-r border-sidebar-border flex flex-col">
      {/* Brand header: CONORBIT → org logo → org name → Dashboard label */}
      <div className="px-5 pt-4 pb-3 border-b border-sidebar-border/50 space-y-3">
        {/* Platform wordmark */}
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md gradient-primary flex items-center justify-center shadow-glow shrink-0">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div>
            <span className="text-[13px] font-bold text-sidebar-foreground leading-none">CONORBIT</span>
            <span className="text-[11px] text-muted-foreground ml-1">Media</span>
          </div>
        </div>

        {/* Org logo + name, or the institution switcher if the user belongs to more than one */}
        <OrgSwitcher orgBrand={orgBrand} />
      </div>

      <nav className="flex-1 px-3 py-2 space-y-0.5 overflow-y-auto">
        {/* Individual (freelance journalist) nav */}
        {orgType === 'individual' ? (
          <>
            <button onClick={() => onViewChange('dashboard')} className={btnClass('dashboard')}>
              <LayoutDashboard className="w-5 h-5" />
              Dashboard
            </button>
            <button onClick={() => onViewChange('media-registry')} className={btnClass('media-registry')}>
              <Contact2 className="w-5 h-5" />
              Media Registry
            </button>
            <button onClick={() => onViewChange('accreditation')} className={btnClass('accreditation')}>
              <FileCheck className="w-5 h-5" />
              Accreditation
            </button>
            <button onClick={() => onViewChange('events')} className={btnClass('events')}>
              <CalendarDays className="w-5 h-5" />
              Events
            </button>
            <button onClick={() => onViewChange('press-releases')} className={btnClass('press-releases')}>
              <Newspaper className="w-5 h-5" />
              Press Releases
            </button>
            <button onClick={() => onViewChange('social-hub')} className={btnClass('social-hub')}>
              <Share2 className="w-5 h-5" />
              Social Hub
            </button>
            <button onClick={() => onViewChange('posts')} className={btnClass('posts')}>
              <PenSquare className="w-5 h-5" />
              Create Post
            </button>
            <button onClick={() => onViewChange('calendar')} className={btnClass('calendar')}>
              <CalendarDays className="w-5 h-5" />
              Content Calendar
            </button>
            <button onClick={() => onViewChange('press-review')} className={btnClass('press-review')}>
              <Users2 className="w-5 h-5" />
              Journalist Portal
            </button>
          </>
        ) : orgType === 'association' ? (
          <>
            <button onClick={() => onViewChange('dashboard')} className={btnClass('dashboard')}>
              <LayoutDashboard className="w-5 h-5" />
              Dashboard
            </button>
            <button onClick={() => onViewChange('assoc-members')} className={btnClass('assoc-members')}>
              <UserCheck className="w-5 h-5" />
              Members
            </button>
            <button onClick={() => onViewChange('assoc-membership')} className={btnClass('assoc-membership')}>
              <Hash className="w-5 h-5" />
              Membership Numbers
            </button>
            <button onClick={() => onViewChange('press-review')} className={btnClass('press-review')}>
              <Users2 className="w-5 h-5" />
              Press Review
            </button>
            <button onClick={() => onViewChange('analytics')} className={btnClass('analytics')}>
              <BarChart3 className="w-5 h-5" />
              Analytics
            </button>
            <button onClick={() => onViewChange('events')} className={btnClass('events')}>
              <CalendarDays className="w-5 h-5" />
              Events
            </button>
            <button onClick={() => onViewChange('organization')} className={btnClass('organization')}>
              <Network className="w-5 h-5" />
              Organisation
            </button>
          </>
        ) : orgId ? (
          INSTITUTION_MODULES.map(item => (
            <PermissionGatedNavItem
              key={`${item.module}-${item.view}`}
              module={item.module}
              level={item.level}
              icon={item.icon}
              label={item.label}
              view={item.view}
              activeView={activeView}
              onViewChange={onViewChange}
              orgId={orgId}
            />
          ))
        ) : (
          INSTITUTION_MODULES.map(item => {
            const Icon = item.icon
            return (
              <button key={item.view} onClick={() => onViewChange(item.view)} className={btnClass(item.view)}>
                <Icon className="w-5 h-5" />
                {item.label}
              </button>
            )
          })
        )}

        {(loading || !orgId || can('organogram', 'full')) && orgType !== 'association' && (
          <button onClick={() => onViewChange('org-settings')} className={btnClass('org-settings')}>
            <SlidersHorizontal className="w-5 h-5" />
            Org Settings
          </button>
        )}

        {orgType === 'association' && (
          <button onClick={() => onViewChange('org-settings')} className={btnClass('org-settings')}>
            <SlidersHorizontal className="w-5 h-5" />
            Settings
          </button>
        )}

        <button onClick={() => onViewChange('tasks')} className={btnClass('tasks')}>
          <CheckSquare className="w-5 h-5" />
          Tasks
        </button>

        <button onClick={() => onViewChange('support')} className={btnClass('support')}>
          <LifeBuoy className="w-5 h-5" />
          Support
        </button>

        <button onClick={() => onViewChange('billing')} className={btnClass('billing')}>
          <CreditCard className="w-5 h-5" />
          Billing
        </button>
      </nav>

      <div className="p-3 border-t border-sidebar-border space-y-1">
        {/* Theme + Language row */}
        <div className="flex items-center gap-1 px-1 pb-1">
          {/* Theme cycle: light → dark → system */}
          <button
            onClick={cycleTheme}
            title={`Theme: ${theme}`}
            className="flex items-center gap-1.5 px-2 py-1.5 rounded-md text-[11px] text-muted-foreground hover:text-sidebar-foreground hover:bg-sidebar-accent/50 transition-colors"
          >
            {themeIcon}
            <span className="capitalize">{mounted ? theme : 'system'}</span>
          </button>

          <div className="w-px h-4 bg-sidebar-border mx-1" />

          {/* Language quick picker */}
          <div className="flex items-center gap-0.5">
            <Globe className="w-3 h-3 text-muted-foreground mr-1" />
            {SUPPORTED_LANGS.map(l => (
              <button
                key={l.code}
                onClick={() => i18n.changeLanguage(l.code)}
                className={cn(
                  'text-[10px] font-medium px-1 py-0.5 rounded transition-colors',
                  i18n.language === l.code
                    ? 'text-sidebar-foreground bg-sidebar-accent'
                    : 'text-muted-foreground hover:text-sidebar-foreground'
                )}
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>

        <button
          onClick={() => onViewChange('settings')}
          className={cn(
            "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-base",
            activeView === 'settings'
              ? "bg-sidebar-accent text-sidebar-accent-foreground shadow-sm"
              : "text-sidebar-foreground hover:bg-sidebar-accent/50"
          )}
        >
          <Settings className="w-5 h-5" />
          {t('nav.settings')}
        </button>

        {isAppAdmin && (
          <button
            onClick={() => navigate('/admin')}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-[11px] font-medium text-[#C8A84B]/80 hover:text-[#C8A84B] hover:bg-[#C8A84B]/10 transition-all"
          >
            <ShieldCheck size={14} className="shrink-0" />
            Platform Administration
          </button>
        )}
      </div>
    </aside>
  );
};
